import axios from "axios";
import { useEffect, useState } from "react";
import { FaWineBottle } from "react-icons/fa";
import {
  MdAccountBalanceWallet,
  MdOutlineCalendarMonth,
  MdOutlineLocalShipping,
  MdPayments,
  MdPeople,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Redem_Das() {
  const navigate = useNavigate();
  const rcId = sessionStorage.getItem("rc_id");
  const centerName = sessionStorage.getItem("center_name");
  const [loading, setLoading] = useState(true);

  const [dashboardData, setDashboardData] = useState({
    rcName: "",
    businessName: "",
    ecEmail: "",
    ein: "",
    todaysPickups: 0,
    pendingPayouts: 0,
    customerCount: 0,
    driverCount: 0,
    weeklyBottles: 0,
    monthlyBottles: 0,
    customerPayouts: 0,
    driverPayouts: 0,
  });

  // ✅ Fetch Dashboard Data
  const fetchDashboardData = async (rcId, centerName) => {
    try {
      setLoading(true);
      const response = await axios.get("/auth/redemption-dashboard", {
        params: { rc_id: rcId, center_name: centerName },
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!rcId || !centerName) {
      toast.error("❌ Missing Redemption Center details. Please log in again.");
      setLoading(false);
      return;
    }
    fetchDashboardData(rcId, centerName);
  }, []);

  // ✅ Combined Metric Cards
  const metricCards = [
    {
      title: "Today's Pickups",
      value: dashboardData.todaysPickups || 0,
      icon: <MdOutlineLocalShipping className="text-4xl" />,
      colorFrom: "from-green-50",
      colorTo: "to-green-100",
      bgIcon: "bg-green-600",
    },
    {
      title: "Pending Payouts",
      value: dashboardData.pendingPayouts || 0,
      icon: <MdPayments className="text-4xl" />,
      colorFrom: "from-yellow-50",
      colorTo: "to-yellow-100",
      bgIcon: "bg-yellow-500",
    },
    {
      title: "Customer Count",
      value: dashboardData.customerCount || 0,
      icon: <MdPeople className="text-4xl" />,
      colorFrom: "from-blue-50",
      colorTo: "to-blue-100",
      bgIcon: "bg-blue-600",
    },
    {
      title: "Driver Count",
      value: dashboardData.driverCount || 0,
      icon: <MdPeople className="text-4xl" />,
      colorFrom: "from-purple-50",
      colorTo: "to-purple-100",
      bgIcon: "bg-purple-600",
    },
    {
      title: "Weekly Total Of Bottles",
      value: dashboardData.weeklyBottles || 0,
      icon: <FaWineBottle className="text-4xl" />,
      colorFrom: "from-emerald-50",
      colorTo: "to-emerald-100",
      bgIcon: "bg-emerald-600",
    },
    {
      title: "Monthly Total Of Bottles",
      value: dashboardData.monthlyBottles || 0,
      icon: <MdOutlineCalendarMonth className="text-4xl" />,
      colorFrom: "from-orange-50",
      colorTo: "to-orange-100",
      bgIcon: "bg-orange-500",
    },
    {
      title: "Customer Payouts",
      value: dashboardData.customerPayouts || 0,
      icon: <MdAccountBalanceWallet className="text-4xl" />,
      colorFrom: "from-cyan-50",
      colorTo: "to-cyan-100",
      bgIcon: "bg-cyan-600",
    },
    {
      title: "Driver Payouts",
      value: dashboardData.driverPayouts || 0,
      icon: <MdOutlineLocalShipping className="text-4xl" />,
      colorFrom: "from-pink-50",
      colorTo: "to-pink-100",
      bgIcon: "bg-pink-600",
    },
  ];

  return (
    <section className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white max-w-8xl mx-auto p-6 sm:p-8 rounded-2xl shadow-lg">
        <div className="p-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
            <h2 className="text-2xl font-bold text-green-800">
              Hi {dashboardData.rcName || "RC Admin"}!
            </h2>
          </div>

          {/* Loader */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-16 h-16 border-4 border-t-transparent border-green-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* RC Info Card */}
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 shadow-md p-4 sm:p-6 rounded-2xl mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div className="flex-shrink-0 bg-indigo-600 text-white p-4 rounded-xl text-lg sm:text-xl font-bold min-w-[60px] text-center">
                  {rcId}
                </div>

                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-8 w-full">
                  <div className="w-full sm:w-auto">
                    <h3 className="text-gray-600 text-xs sm:text-sm font-medium">Center Name</h3>
                    <p className="text-base sm:text-lg font-bold text-gray-700 break-words">{dashboardData.rcName}</p>
                  </div>

                  <div className="w-full sm:w-auto">
                    <h3 className="text-gray-600 text-xs sm:text-sm font-medium">Business / Legal Name</h3>
                    <p className="text-base sm:text-lg font-bold text-gray-700 break-words">{dashboardData.businessName}</p>
                  </div>

                  <div className="w-full sm:w-auto">
                    <h3 className="text-gray-600 text-xs sm:text-sm font-medium">EIN / Business Reg. No.</h3>
                    <p className="text-base sm:text-lg font-bold text-gray-700">{dashboardData.ein}</p>
                  </div>

                  <div className="w-full sm:w-auto">
                    <h3 className="text-gray-600 text-xs sm:text-sm font-medium">Email</h3>
                    <p className="text-base sm:text-lg font-bold text-gray-700 break-words">{dashboardData.ecEmail}</p>
                  </div>
                </div>
              </div>

              {/* Metric Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                {metricCards.map((card, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center bg-gradient-to-r ${card.colorFrom} ${card.colorTo} shadow-lg p-6 rounded-3xl`}
                  >
                    <div className={`flex-shrink-0 ${card.bgIcon} text-white p-4 rounded-xl`}>
                      {card.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-gray-600 text-sm font-medium">{card.title}</h3>
                      <p className="text-3xl font-extrabold text-gray-800">{card.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>
    </section>
  );
}
